import {
    calculateResidualSquareCost,
    solveDampedLeastSquaresStep,
} from '../../../numeric/dampedLeastSquares';
import {SketchParametricJacobian} from './SketchParametricJacobian';
import type {
    SketchParametricEquation,
    SketchParametricResidualUnit,
    SketchParametricSolveResult,
} from '../../../../types/sketch/solver/SketchParametricTypes';
import type {SketchParametricParameterVector} from './SketchParametricParameterVector';
import {SKETCH_PARAMETRIC_TOLERANCE} from './SketchParametricToleranceConfig';
import {SketchParametricNullSpaceProjector} from './SketchParametricNullSpaceProjector';

/**
 * 草图 Levenberg-Marquardt 最小二乘求解器。
 *
 * 设计说明：
 * 1. LM 步长/成本/线性解消费通用 numeric 模块（02 计划 §7.2 单一数值来源，
 *    草图不依赖装配体命名空间）；
 * 2. 求解从 seed（当前参数值）出发，天然承载多解分支延续语义（§7.5）；
 * 3. 不收敛不写回由调用方决定：本类只迭代参数向量并报告收敛状态。
 */
export class SketchParametricLeastSquaresSolver {
    /** 最大迭代次数。 */
    private readonly maxIterations: number;

    /** 最大容差满足比；1 表示所有残差进入各自量纲容差。 */
    private readonly residualTolerance: number;

    /** 初始阻尼因子。 */
    private readonly initialDamping: number;

    /** 数值雅可比计算器。 */
    private readonly jacobianCalculator = new SketchParametricJacobian();

    /** 硬约束 Jacobian 零空间计算器。 */
    private readonly nullSpaceProjector = new SketchParametricNullSpaceProjector();

    /**
     * @param options 求解选项。
     */
    public constructor(options?: {
        readonly maxIterations?: number;
        readonly residualTolerance?: number;
        readonly initialDamping?: number;
    }) {
        this.maxIterations = options?.maxIterations ?? 200;
        this.residualTolerance = options?.residualTolerance ?? SKETCH_PARAMETRIC_TOLERANCE.residual;
        this.initialDamping = options?.initialDamping ?? 1e-3;
    }

    /**
     * 从当前参数值（seed）出发执行硬约束/偏好分层求解。
     *
     * @param parameters 参数向量（就地更新）。
     * @param equations 残差方程列表。
     * @param shouldCancel 迭代取消检查；返回 `true` 时立即停止。
     * @returns 求解结果。
     */
    public solve(
        parameters: SketchParametricParameterVector,
        equations: readonly SketchParametricEquation[],
        shouldCancel?: () => boolean,
    ): SketchParametricSolveResult {
        const hardEquations = equations.filter((equation) => equation.strength !== 'preference');
        const preferenceEquations = equations.filter((equation) => equation.strength === 'preference');
        const hardResult = this.solveEquationSet(parameters, hardEquations, shouldCancel);

        if (!hardResult.converged || preferenceEquations.length === 0) {
            return hardResult;
        }

        /**
         * 鼠标目标虽然属于瞬态偏好，但当它与全部硬约束存在共同可行解时，
         * CAD 交互必须精确到达目标，而不能因为零空间基的列顺序或数值截断停在附近。
         * 操作目标先按直接点位移平移自由坐标初值、把直接参数覆盖为输入值并临时固定，
         * 再只求解其余硬关系；没有 seed 协议的通用偏好仍与硬约束联合求解。若候选不可达，则恢复硬约束可行 seed，
         * 沿原有零空间路径求最近可行位置，保持固定/尺寸/几何关系绝不被软化。
         */
        const hardFeasibleSeed = parameters.snapshotValues();
        const fixedStateSeed = parameters.snapshotFixedStates();
        const directPreferenceEquations = preferenceEquations.filter((equation) => (
            (equation.preferenceSeedBindings?.length ?? 0) > 0
        ));
        const temporaryConstraintEquations = preferenceEquations.filter((equation) => (
            (equation.preferenceSeedBindings?.length ?? 0) === 0
        ));
        const seededTargetParameterIds = this.applyPreferenceSeeds(parameters, directPreferenceEquations);

        seededTargetParameterIds.forEach((parameterId) => parameters.setFixed(parameterId, true));
        const exactCandidateEquations = seededTargetParameterIds.size > 0
            ? hardEquations
            : [...hardEquations, ...preferenceEquations];
        let exactTargetResult = this.solveEquationSet(
            parameters,
            exactCandidateEquations,
            shouldCancel,
            SKETCH_PARAMETRIC_TOLERANCE.preferenceFeasibilityMaxIterations,
        );
        let exactTargetIterations = exactTargetResult.iterations;

        /**
         * 直接目标可能位于由退化几何分隔的另一条可行分支。例如线段端点越过后，
         * 从 pointerdown seed 做局部 LM 只能无限逼近零长奇点。连续 seed 失败时，
         * 再以直接目标的整体位移构造一个候选起点；它只改变搜索初值，最终仍必须
         * 同时严格满足全部硬关系和直接目标，因此不会软化约束或接受折中几何。
         */
        if (!exactTargetResult.converged
            && exactTargetResult.terminationReason !== 'cancelled'
            && seededTargetParameterIds.size > 0) {
            parameters.restoreValues(hardFeasibleSeed);
            parameters.restoreFixedStates(fixedStateSeed);
            const translatedTargetParameterIds = this.applyTranslatedPreferenceSeeds(
                parameters,
                directPreferenceEquations,
            );

            translatedTargetParameterIds.forEach((parameterId) => parameters.setFixed(parameterId, true));
            const translatedTargetResult = this.solveEquationSet(
                parameters,
                hardEquations,
                shouldCancel,
                SKETCH_PARAMETRIC_TOLERANCE.preferenceFeasibilityMaxIterations,
            );

            exactTargetIterations += translatedTargetResult.iterations;
            exactTargetResult = translatedTargetResult;
        }

        /**
         * FreeCAD 把正式草图约束与负标签临时拖拽约束分成两个优先级子系统。
         * 这里使用现有参数内核表达同等语义：鼠标目标先临时固定，再尝试把
         * 线长等临时约束与正式硬约束联合收敛。只有联合可行时才把临时约束
         * 提升为当前拖拽帧的可行流形；不可行时恢复精确鼠标目标解并降级为偏好。
         */
        let activeConstraintEquations = exactCandidateEquations;
        let activeConstraintResult = exactTargetResult;
        let temporaryConstraintIterations = 0;
        let stabilityPreferenceEquations = this.createSeedStabilityEquations(
            parameters,
            hardFeasibleSeed,
        );

        if (exactTargetResult.converged
            && seededTargetParameterIds.size > 0
            && temporaryConstraintEquations.length > 0) {
            const pointPositionConstraints = temporaryConstraintEquations.filter((equation) => (
                equation.kind === 'temporary-drag-point-position'
            ));
            const parameterRoleById = new Map(parameters.getDescriptors().map((descriptor) => (
                [descriptor.parameterId, descriptor.role] as const
            )));
            const pointPositionConstraintGroups = (['x', 'y'] as const)
                .map((coordinateRole) => pointPositionConstraints.filter((equation) => (
                    parameterRoleById.get(equation.parameterIds[0] ?? '') === coordinateRole
                )))
                .filter((group) => group.length > 0);
            const unclassifiedPointPositionConstraints = pointPositionConstraints.filter((equation) => {
                const parameterRole = parameterRoleById.get(equation.parameterIds[0] ?? '');

                return parameterRole !== 'x' && parameterRole !== 'y';
            });
            const passiveLineLengthConstraints = temporaryConstraintEquations.filter((equation) => (
                equation.kind === 'temporary-drag-line-length'
            ));
            const passiveLineMidpointConstraints = temporaryConstraintEquations.filter((equation) => (
                equation.kind === 'temporary-drag-line-midpoint'
            ));
            const temporaryConstraintGroups: readonly (readonly SketchParametricEquation[])[] = [
                ...pointPositionConstraintGroups,
                ...(unclassifiedPointPositionConstraints.length > 0
                    ? [unclassifiedPointPositionConstraints]
                    : []),
                ...(passiveLineLengthConstraints.length > 0 ? [passiveLineLengthConstraints] : []),
                ...(passiveLineMidpointConstraints.length > 0 ? [passiveLineMidpointConstraints] : []),
            ];

            /**
             * 直接拖动线的另一端点位置优先于被关系间接驱动实体的形状，因而按 x/y
             * 坐标层分别检查全部对端点。被动直线先整体保长，再整体保持中点：长度定义形状
             * 与翻转分支，中点只负责欠定义位置；若把两者混为一个不可拆包，中点冲突
             * 会连带丢掉仍可满足的保长分支。每一层都按全集检查，避免实体遍历顺序
             * 赋予某条线更高优先级；不可行层整体降为偏好，由 seed 稳定层选择连续解。
             */
            for (const temporaryConstraintGroup of temporaryConstraintGroups) {
                if (activeConstraintResult.terminationReason === 'cancelled') {
                    break;
                }
                const acceptedConstraintSeed = parameters.snapshotValues();
                const candidateEquations = [
                    ...activeConstraintEquations,
                    ...temporaryConstraintGroup,
                ];
                const temporaryConstraintResult = this.solveEquationSet(
                    parameters,
                    candidateEquations,
                    shouldCancel,
                    SKETCH_PARAMETRIC_TOLERANCE.preferenceFeasibilityMaxIterations,
                );

                temporaryConstraintIterations += temporaryConstraintResult.iterations;
                if (temporaryConstraintResult.converged) {
                    activeConstraintEquations = candidateEquations;
                    activeConstraintResult = temporaryConstraintResult;
                    continue;
                }

                parameters.restoreValues(acceptedConstraintSeed);
                if (temporaryConstraintResult.terminationReason === 'cancelled') {
                    activeConstraintResult = temporaryConstraintResult;
                    break;
                }
                stabilityPreferenceEquations = [
                    ...temporaryConstraintGroup,
                    ...stabilityPreferenceEquations,
                ];
            }
        }

        /**
         * 严格目标固定后，欠定义硬系统仍可能有多组解。以拖拽前硬可行 seed 为第二层偏好，
         * 在保持目标精确和硬关系成立的前提下选择总参数改变量最小的连续分支。
         */
        const refinedExactResult = activeConstraintResult.converged && seededTargetParameterIds.size > 0
            ? this.optimizePreferences(
                parameters,
                activeConstraintEquations,
                stabilityPreferenceEquations,
                activeConstraintResult,
                shouldCancel,
            )
            : activeConstraintResult;

        // 临时固定只属于候选求解，无论成功、失败或取消都不能泄漏到正式参数状态。
        parameters.restoreFixedStates(fixedStateSeed);
        const exactDirectPreferenceEvaluation = this.jacobianCalculator.evaluateResiduals(
            parameters,
            directPreferenceEquations,
            parameters.calculateCharacteristicLength(),
        );
        const exactPreferenceEvaluation = this.jacobianCalculator.evaluateResiduals(
            parameters,
            preferenceEquations,
            parameters.calculateCharacteristicLength(),
        );

        const exactPreferenceIterations = exactTargetIterations
            + temporaryConstraintIterations
            + (refinedExactResult.preferenceIterations ?? 0);

        if (refinedExactResult.terminationReason === 'cancelled') {
            return {
                ...refinedExactResult,
                preferenceCost: calculateResidualSquareCost(exactPreferenceEvaluation.optimizationResiduals),
                preferenceMaxResidual: exactPreferenceEvaluation.maxSatisfactionRatio,
                preferenceIterations: exactPreferenceIterations,
            };
        }
        if (refinedExactResult.converged
            && exactDirectPreferenceEvaluation.maxSatisfactionRatio <= this.residualTolerance) {
            return {
                ...refinedExactResult,
                preferenceCost: calculateResidualSquareCost(exactPreferenceEvaluation.optimizationResiduals),
                preferenceMaxResidual: exactPreferenceEvaluation.maxSatisfactionRatio,
                preferenceIterations: exactPreferenceIterations,
            };
        }

        // 联合候选不可达时必须回到纯硬约束可行点，禁止残留半次迭代。
        parameters.restoreValues(hardFeasibleSeed);
        const blockedPreferenceEvaluation = this.jacobianCalculator.evaluateResiduals(
            parameters,
            directPreferenceEquations,
            parameters.calculateCharacteristicLength(),
        );

        /**
         * 只有严格候选失败后才计算零空间可动性，避免给每个正常 pointermove
         * 额外增加一次完整 Jacobian 分解。当直接目标 Jacobian 在全部硬约束
         * 零空间上的投影为零时，Fix/FixSlot、固定轴或尺寸闭包已消除目标的
         * 全部一阶自由度；此时“最近可行”只会忽略鼠标目标，必须明确阻断。
         */
        if (directPreferenceEquations.length > 0
            && blockedPreferenceEvaluation.maxSatisfactionRatio > this.residualTolerance
            && !this.hasDirectTargetMobility(parameters, hardEquations, directPreferenceEquations)) {
            return {
                ...hardResult,
                converged: false,
                maxResidual: blockedPreferenceEvaluation.maxSatisfactionRatio,
                preferenceCost: calculateResidualSquareCost(
                    blockedPreferenceEvaluation.optimizationResiduals,
                ),
                preferenceMaxResidual: blockedPreferenceEvaluation.maxSatisfactionRatio,
                preferenceIterations: 0,
                terminationReason: 'blocked',
            };
        }

        // 仍有可动分量时保留普通 CAD 最近可行拖拽，不把部分约束误判为完全固定。
        return this.optimizePreferences(
            parameters,
            hardEquations,
            preferenceEquations,
            hardResult,
            shouldCancel,
        );
    }

    /**
     * 判断直接操作目标在硬约束可行流形上是否仍有一阶自由度。
     *
     * 这里不沿某一种关系传播固定标记，而是计算硬约束 Jacobian 的零空间，
     * 再检查操作目标 Jacobian 在该零空间上的投影。只要任一目标分量可动，
     * 就保留普通 CAD 最近可行拖拽；全部投影为零才表示目标被硬闭包完全锁定。
     *
     * @param parameters 当前硬约束可行参数。
     * @param hardEquations 正式关系、尺寸与结构方程。
     * @param directPreferenceEquations 带直接 seed 绑定的操作目标方程。
     * @returns 至少一个目标分量可沿硬约束流形变化时返回 true。
     */
    private hasDirectTargetMobility(
        parameters: SketchParametricParameterVector,
        hardEquations: readonly SketchParametricEquation[],
        directPreferenceEquations: readonly SketchParametricEquation[],
    ): boolean {
        if (directPreferenceEquations.length === 0) {
            return true;
        }
        const freeParameterIds = parameters.getFreeParameterIds();

        if (freeParameterIds.length === 0) {
            return false;
        }
        const characteristicLength = parameters.calculateCharacteristicLength();
        const hardJacobian = this.jacobianCalculator.compute(
            parameters,
            hardEquations,
            freeParameterIds,
            characteristicLength,
        );
        const hardNullSpaceBasis = this.nullSpaceProjector.compute(
            hardJacobian,
            freeParameterIds.length,
        );

        if (hardNullSpaceBasis.length === 0) {
            return false;
        }
        const targetJacobian = this.jacobianCalculator.compute(
            parameters,
            directPreferenceEquations,
            freeParameterIds,
            characteristicLength,
        );

        return targetJacobian.some((targetRow) => hardNullSpaceBasis.some((basisVector) => (
            Math.abs(targetRow.reduce((projection, derivative, parameterIndex) => (
                projection + derivative * (basisVector[parameterIndex] ?? 0)
            ), 0)) > SKETCH_PARAMETRIC_TOLERANCE.nullSpacePivot
        )));
    }

    /**
     * 应用偏好方程声明的严格候选初始值。
     *
     * 操作目标方程会把被拖参数绑定到固定鼠标参数。FreeCAD 拖拽每帧都从
     * `initSolution()` 捕获的 reference 开始，只改写负标签临时约束的目标参数。
     * 因此这里只精确复制直接目标，禁止再把平均鼠标位移预先施加到整个自由参数岛。
     *
     * @param parameters 参数向量。
     * @param preferenceEquations 操作偏好方程集合。
     * @returns 已成功预置、需要在严格候选期间临时固定的目标参数 ID。
     */
    private applyPreferenceSeeds(
        parameters: SketchParametricParameterVector,
        preferenceEquations: readonly SketchParametricEquation[],
    ): ReadonlySet<string> {
        const seededTargetParameterIds = new Set<string>();
        const validBindings = preferenceEquations.flatMap((equation) => (
            equation.preferenceSeedBindings ?? []
        )).filter((binding) => (
            parameters.has(binding.targetParameterId)
            && parameters.has(binding.sourceParameterId)
            && !parameters.isFixed(binding.targetParameterId)
            && parameters.isFixed(binding.sourceParameterId)
        ));
        // 直接目标只改写自身参数，其余参数保持拖拽会话基线作为求解初值。
        validBindings.forEach((binding) => {
            parameters.write(
                binding.targetParameterId,
                parameters.read(binding.sourceParameterId),
            );
            seededTargetParameterIds.add(binding.targetParameterId);
        });

        return seededTargetParameterIds;
    }

    /**
     * 为跨退化分支的第二候选构造整体位移 seed。
     *
     * 该入口只在“直接目标固定 + 基线局部 seed”不收敛后调用。它按 x/y 分量
     * 汇总全部直接目标位移，并把平均位移施加到当前可写坐标参数，随后覆盖精确
     * 目标。非坐标参数与领域固定参数保持不变，候选失败时由调用方恢复硬可行基线。
     *
     * @param parameters 已恢复到硬约束可行基线的参数向量。
     * @param preferenceEquations 带严格 seed 绑定的直接操作偏好。
     * @returns 已写入直接目标、需要在候选期间临时固定的参数 ID。
     */
    private applyTranslatedPreferenceSeeds(
        parameters: SketchParametricParameterVector,
        preferenceEquations: readonly SketchParametricEquation[],
    ): ReadonlySet<string> {
        const descriptorById = new Map(
            parameters.getDescriptors().map((descriptor) => (
                [descriptor.parameterId, descriptor] as const
            )),
        );
        const validBindings = preferenceEquations.flatMap((equation) => (
            equation.preferenceSeedBindings ?? []
        )).filter((binding) => (
            parameters.has(binding.targetParameterId)
            && parameters.has(binding.sourceParameterId)
            && !parameters.isFixed(binding.targetParameterId)
            && parameters.isFixed(binding.sourceParameterId)
        ));
        const coordinateDeltaByRole = new Map<'x' | 'y', number[]>([
            ['x', []],
            ['y', []],
        ]);

        validBindings.forEach((binding) => {
            const targetRole = descriptorById.get(binding.targetParameterId)?.role;

            if (targetRole !== 'x' && targetRole !== 'y') {
                return;
            }
            coordinateDeltaByRole.get(targetRole)!.push(
                parameters.read(binding.sourceParameterId) - parameters.read(binding.targetParameterId),
            );
        });

        coordinateDeltaByRole.forEach((coordinateDeltas, role) => {
            if (coordinateDeltas.length === 0) {
                return;
            }
            const averageDelta = coordinateDeltas.reduce((sum, delta) => sum + delta, 0)
                / coordinateDeltas.length;

            parameters.getDescriptors().forEach((descriptor) => {
                if (descriptor.role === role && !parameters.isFixed(descriptor.parameterId)) {
                    parameters.write(descriptor.parameterId, descriptor.value + averageDelta);
                }
            });
        });

        const seededTargetParameterIds = new Set<string>();

        validBindings.forEach((binding) => {
            parameters.write(
                binding.targetParameterId,
                parameters.read(binding.sourceParameterId),
            );
            seededTargetParameterIds.add(binding.targetParameterId);
        });

        return seededTargetParameterIds;
    }

    /**
     * 创建严格目标候选的第二层 seed 稳定偏好。
     *
     * 当前临时固定参数（直接鼠标目标和领域 Fix）不进入偏好；其余自由参数分别趋近
     * 纯硬约束可行 seed。残差量纲按参数角色声明，保证坐标、角度和无量纲曲线参数
     * 使用统一归一化规则，不通过任意权重交换物理量。
     *
     * @param parameters 当前参数向量。
     * @param seed 严格目标应用前的纯硬约束可行参数值。
     * @returns 每个自由参数一条的 seed 稳定偏好方程。
     */
    private createSeedStabilityEquations(
        parameters: SketchParametricParameterVector,
        seed: Readonly<Record<string, number>>,
    ): readonly SketchParametricEquation[] {
        return parameters.getDescriptors()
            .filter((descriptor) => (
                !parameters.isFixed(descriptor.parameterId)
                && seed[descriptor.parameterId] !== undefined
            ))
            .map((descriptor) => {
                const residualUnit: SketchParametricResidualUnit = descriptor.role === 'angle'
                    ? 'angle'
                    : descriptor.role === 'scale' || descriptor.role === 'curve-parameter'
                        ? 'dimensionless'
                        : 'length';
                const targetValue = seed[descriptor.parameterId]!;

                return {
                    equationId: `preference-seed:${descriptor.parameterId}`,
                    kind: 'parameter-seed-stability',
                    residualCount: 1,
                    residualUnits: [residualUnit],
                    parameterIds: [descriptor.parameterId],
                    branchSign: null,
                    strength: 'preference',
                    evaluateJacobian: () => [[1]],
                    evaluate: (read) => [read(descriptor.parameterId) - targetValue],
                };
            });
    }

    /**
     * 把一组同层级方程作为普通 LM 系统求解。
     *
     * @param parameters 参数向量（就地更新）。
     * @param equations 同层级方程列表。
     * @param shouldCancel 取消检查。
     * @param maxIterations 本次方程集允许的最大迭代次数。
     * @returns 该层求解结果。
     */
    private solveEquationSet(
        parameters: SketchParametricParameterVector,
        equations: readonly SketchParametricEquation[],
        shouldCancel?: () => boolean,
        maxIterations: number = this.maxIterations,
    ): SketchParametricSolveResult {
        if (shouldCancel?.()) {
            return {
                converged: false,
                iterations: 0,
                maxResidual: Number.POSITIVE_INFINITY,
                cost: Number.POSITIVE_INFINITY,
                terminationReason: 'cancelled',
            };
        }
        const freeParameterIds = parameters.getFreeParameterIds();
        const characteristicLength = parameters.calculateCharacteristicLength();

        let residualEvaluation = this.jacobianCalculator.evaluateResiduals(
            parameters,
            equations,
            characteristicLength,
        );
        let residuals = [...residualEvaluation.optimizationResiduals];
        let cost = calculateResidualSquareCost(residuals);
        let maxResidual = residualEvaluation.maxSatisfactionRatio;
        let damping = this.initialDamping;

        // 无自由参数时只评估残差（全固定系统的可满足性检查）。
        if (freeParameterIds.length === 0) {
            return {
                converged: maxResidual <= this.residualTolerance,
                iterations: 0,
                maxResidual,
                cost,
                rawResidualByUnit: residualEvaluation.maxRawResidualByUnit,
                terminationReason: maxResidual <= this.residualTolerance ? 'converged' : 'singular',
            };
        }

        for (let iteration = 1; iteration <= maxIterations; iteration += 1) {
            if (shouldCancel?.()) {
                return {
                    converged: false,
                    iterations: iteration - 1,
                    maxResidual,
                    cost,
                    rawResidualByUnit: residualEvaluation.maxRawResidualByUnit,
                    terminationReason: 'cancelled',
                };
            }
            if (maxResidual <= this.residualTolerance) {
                return {
                    converged: true,
                    iterations: iteration - 1,
                    maxResidual,
                    cost,
                    rawResidualByUnit: residualEvaluation.maxRawResidualByUnit,
                    terminationReason: 'converged',
                };
            }

            const jacobian = this.jacobianCalculator.compute(
                parameters,
                equations,
                freeParameterIds,
                characteristicLength,
            );
            const step = solveDampedLeastSquaresStep(jacobian, residuals, damping);

            if (!step) {
                // 法方程奇异：提高阻尼后重试。
                damping *= 10;
                if (damping > 1e10) {
                    return {
                        converged: false,
                        iterations: iteration,
                        maxResidual,
                        cost,
                        rawResidualByUnit: residualEvaluation.maxRawResidualByUnit,
                        terminationReason: 'singular',
                    };
                }
                continue;
            }

            // 试探步：记录旧值，接受则保留，拒绝则回退并加大阻尼。
            const previousValues = freeParameterIds.map((parameterId) => parameters.read(parameterId));

            freeParameterIds.forEach((parameterId, index) => {
                parameters.write(parameterId, previousValues[index]! + step[index]!);
            });

            const nextResidualEvaluation = this.jacobianCalculator.evaluateResiduals(
                parameters,
                equations,
                characteristicLength,
            );
            const nextResiduals = [...nextResidualEvaluation.optimizationResiduals];
            const nextCost = calculateResidualSquareCost(nextResiduals);

            if (nextCost < cost) {
                residualEvaluation = nextResidualEvaluation;
                residuals = nextResiduals;
                cost = nextCost;
                maxResidual = residualEvaluation.maxSatisfactionRatio;
                damping = Math.max(damping * 0.3, SKETCH_PARAMETRIC_TOLERANCE.minimumDamping);
            } else {
                // 拒绝试探步，回退参数。
                freeParameterIds.forEach((parameterId, index) => {
                    parameters.write(parameterId, previousValues[index]!);
                });
                damping *= 10;
                if (damping > 1e10) {
                    return {
                        converged: false,
                        iterations: iteration,
                        maxResidual,
                        cost,
                        rawResidualByUnit: residualEvaluation.maxRawResidualByUnit,
                        terminationReason: 'singular',
                    };
                }
            }
        }

        return {
            converged: maxResidual <= this.residualTolerance,
            iterations: maxIterations,
            maxResidual,
            cost,
            rawResidualByUnit: residualEvaluation.maxRawResidualByUnit,
            terminationReason: maxResidual <= this.residualTolerance ? 'converged' : 'max-iterations',
        };
    }

    /**
     * 在硬约束可行域内最小化操作偏好。
     *
     * 算法：每轮计算硬约束 Jacobian 的零空间，把偏好 Jacobian 投影到该零空间求最小范数步长，
     * 再用硬约束 LM 把非线性二阶漂移投影回可行流形。只有硬约束重新收敛且偏好成本下降时才接受，
     * 因此不存在“给硬约束较大权重”造成的静默放松。
     *
     * @param parameters 参数向量。
     * @param hardEquations 必须严格满足的方程。
     * @param preferenceEquations 只在可行域内优化的方程。
     * @param initialHardResult 初始硬约束可行解。
     * @param shouldCancel 取消检查。
     * @returns 保持硬约束可行并携带偏好诊断的结果。
     */
    private optimizePreferences(
        parameters: SketchParametricParameterVector,
        hardEquations: readonly SketchParametricEquation[],
        preferenceEquations: readonly SketchParametricEquation[],
        initialHardResult: SketchParametricSolveResult,
        shouldCancel?: () => boolean,
    ): SketchParametricSolveResult {
        const freeParameterIds = parameters.getFreeParameterIds();
        const characteristicLength = parameters.calculateCharacteristicLength();
        let hardResult = initialHardResult;
        let preferenceEvaluation = this.jacobianCalculator.evaluateResiduals(
            parameters,
            preferenceEquations,
            characteristicLength,
        );
        let preferenceResiduals = [...preferenceEvaluation.optimizationResiduals];
        let preferenceCost = calculateResidualSquareCost(preferenceResiduals);
        let preferenceIterations = 0;
        let damping = this.initialDamping;

        for (let iteration = 1;
            iteration <= SKETCH_PARAMETRIC_TOLERANCE.preferenceMaxIterations;
            iteration += 1) {
            if (shouldCancel?.()) {
                return {
                    ...hardResult,
                    converged: false,
                    terminationReason: 'cancelled',
                    preferenceCost,
                    preferenceMaxResidual: preferenceEvaluation.maxSatisfactionRatio,
                    preferenceIterations,
                };
            }
            if (preferenceEvaluation.maxSatisfactionRatio <= this.residualTolerance
                || freeParameterIds.length === 0) {
                break;
            }

            const hardJacobian = hardEquations.length > 0
                ? this.jacobianCalculator.compute(
                    parameters,
                    hardEquations,
                    freeParameterIds,
                    characteristicLength,
                )
                : [];
            const nullSpaceBasis = this.nullSpaceProjector.compute(
                hardJacobian,
                freeParameterIds.length,
            );

            if (nullSpaceBasis.length === 0) {
                break;
            }

            const preferenceJacobian = this.jacobianCalculator.compute(
                parameters,
                preferenceEquations,
                freeParameterIds,
                characteristicLength,
            );
            const reducedJacobian = preferenceJacobian.map((row) => (
                nullSpaceBasis.map((basisVector) => row.reduce((sum, derivative, parameterIndex) => (
                    sum + derivative * (basisVector[parameterIndex] ?? 0)
                ), 0))
            ));
            const reducedStep = solveDampedLeastSquaresStep(
                reducedJacobian,
                preferenceResiduals,
                damping,
            );

            if (!reducedStep) {
                damping *= 10;
                if (damping > 1e10) {
                    break;
                }
                continue;
            }

            const parameterStep = freeParameterIds.map((_parameterId, parameterIndex) => (
                nullSpaceBasis.reduce((sum, basisVector, basisIndex) => (
                    sum + (basisVector[parameterIndex] ?? 0) * (reducedStep[basisIndex] ?? 0)
                ), 0)
            ));
            const maxStep = parameterStep.reduce(
                (maximum, value) => Math.max(maximum, Math.abs(value)),
                0,
            );

            if (maxStep <= SKETCH_PARAMETRIC_TOLERANCE.preferenceStep) {
                break;
            }

            const beforeStep = parameters.snapshotValues();

            freeParameterIds.forEach((parameterId, parameterIndex) => {
                parameters.write(parameterId, parameters.read(parameterId) + parameterStep[parameterIndex]!);
            });

            const projectedHardResult = this.solveEquationSet(parameters, hardEquations, shouldCancel);

            if (projectedHardResult.terminationReason === 'cancelled') {
                parameters.restoreValues(beforeStep);
                return {
                    ...hardResult,
                    converged: false,
                    terminationReason: 'cancelled',
                    preferenceCost,
                    preferenceMaxResidual: preferenceEvaluation.maxSatisfactionRatio,
                    preferenceIterations,
                };
            }
            if (!projectedHardResult.converged) {
                parameters.restoreValues(beforeStep);
                damping *= 10;
                if (damping > 1e10) {
                    break;
                }
                continue;
            }

            const nextPreferenceEvaluation = this.jacobianCalculator.evaluateResiduals(
                parameters,
                preferenceEquations,
                characteristicLength,
            );
            const nextPreferenceResiduals = [...nextPreferenceEvaluation.optimizationResiduals];
            const nextPreferenceCost = calculateResidualSquareCost(nextPreferenceResiduals);

            if (nextPreferenceCost < preferenceCost) {
                hardResult = projectedHardResult;
                preferenceEvaluation = nextPreferenceEvaluation;
                preferenceResiduals = nextPreferenceResiduals;
                preferenceCost = nextPreferenceCost;
                preferenceIterations = iteration;
                damping = Math.max(damping * 0.3, SKETCH_PARAMETRIC_TOLERANCE.minimumDamping);
            } else {
                parameters.restoreValues(beforeStep);
                damping *= 10;
                if (damping > 1e10) {
                    break;
                }
            }
        }

        return {
            ...hardResult,
            preferenceCost,
            preferenceMaxResidual: preferenceEvaluation.maxSatisfactionRatio,
            preferenceIterations,
        };
    }
}
